/**
 * UI state store for managing loading, errors, and modals.
 */

import { create } from 'zustand';

interface UIState {
  // Loading states
  isLoading: boolean;
  loadingMessage: string | null;

  // Error handling
  error: string | null;
  validationErrors: Record<string, string[]>;

  // Modal states
  isExitConfirmOpen: boolean;
  isHelpOpen: boolean;

  // Toast/notification
  toast: ToastMessage | null;

  // Device info
  isMobile: boolean;
  screenWidth: number;

  // Actions
  setLoading: (loading: boolean, message?: string) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  setValidationError: (questionCode: string, errors: string[]) => void;
  clearValidationError: (questionCode: string) => void;
  clearAllValidationErrors: () => void;
  getValidationErrors: (questionCode: string) => string[];
  hasValidationErrors: () => boolean;
  openExitConfirm: () => void;
  closeExitConfirm: () => void;
  openHelp: () => void;
  closeHelp: () => void;
  showToast: (toast: ToastMessage) => void;
  clearToast: () => void;
  updateDeviceInfo: () => void;
  reset: () => void;
}

interface ToastMessage {
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
}

export const useUIStore = create<UIState>((set, get) => ({
  // Initial state
  isLoading: false,
  loadingMessage: null,
  error: null,
  validationErrors: {},
  isExitConfirmOpen: false,
  isHelpOpen: false,
  toast: null,
  isMobile: typeof window !== 'undefined' ? window.innerWidth < 768 : false,
  screenWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,

  // Set loading state
  setLoading: (loading: boolean, message?: string) => {
    set({
      isLoading: loading,
      loadingMessage: loading ? message || null : null,
    });
  },

  // Set error
  setError: (error: string | null) => {
    set({ error });
  },

  // Clear error
  clearError: () => {
    set({ error: null });
  },

  // Set validation error for a question
  setValidationError: (questionCode: string, errors: string[]) => {
    set((state) => ({
      validationErrors: {
        ...state.validationErrors,
        [questionCode]: errors,
      },
    }));
  },

  // Clear validation error for a question
  clearValidationError: (questionCode: string) => {
    set((state) => {
      const newErrors = { ...state.validationErrors };
      delete newErrors[questionCode];
      return { validationErrors: newErrors };
    });
  },

  // Clear all validation errors
  clearAllValidationErrors: () => {
    set({ validationErrors: {} });
  },

  // Get validation errors for a question
  getValidationErrors: (questionCode: string) => {
    return get().validationErrors[questionCode] || [];
  },

  // Check if there are any validation errors
  hasValidationErrors: () => {
    return Object.keys(get().validationErrors).length > 0;
  },

  // Modal controls
  openExitConfirm: () => set({ isExitConfirmOpen: true }),
  closeExitConfirm: () => set({ isExitConfirmOpen: false }),
  openHelp: () => set({ isHelpOpen: true }),
  closeHelp: () => set({ isHelpOpen: false }),

  // Toast
  showToast: (toast: ToastMessage) => {
    set({ toast });
    // Auto-clear after duration
    if (toast.duration !== 0) {
      setTimeout(() => {
        set((state) => (state.toast === toast ? { toast: null } : {}));
      }, toast.duration || 3000);
    }
  },

  clearToast: () => {
    set({ toast: null });
  },

  // Update device info
  updateDeviceInfo: () => {
    if (typeof window !== 'undefined') {
      set({
        isMobile: window.innerWidth < 768,
        screenWidth: window.innerWidth,
      });
    }
  },

  // Reset
  reset: () => {
    set({
      isLoading: false,
      loadingMessage: null,
      error: null,
      validationErrors: {},
      isExitConfirmOpen: false,
      isHelpOpen: false,
      toast: null,
    });
  },
}));

// Initialize device info listener
if (typeof window !== 'undefined') {
  window.addEventListener('resize', () => {
    useUIStore.getState().updateDeviceInfo();
  });
}
